/**
 * ENG-1981: how a URL in one scheduler maps to the same place in the other. One pure
 * mapping, shared by the route gate, the switch flow and the side menu.
 */
const CLASSIC = "/scheduling";
const V3 = "/scheduling-v3";

/** Pages both schedulers have. Anything else goes to the other side's grid. */
const SHARED_PAGES = new Set(["room-traffic", "view", "day-sheet"]);

/**
 * `location.state` keys the classic scheduler reads on arrival
 * (`features/scheduling/scheduling.component.tsx`): the chart's reschedule and follow-up
 * hand-offs. V3 cannot take them, so a visit that carries one stays in classic.
 */
const CLASSIC_HANDOFF_KEYS = [
  "rescheduleAppointmentData",
  "followUpAppointmentData",
  "patient"
];

export const isV3Path = (pathname: string): boolean =>
  pathname === V3 || pathname.startsWith(`${V3}/`);

export const isClassicPath = (pathname: string): boolean =>
  pathname === CLASSIC || pathname.startsWith(`${CLASSIC}/`);

const subPage = (pathname: string, base: string): string =>
  pathname
    .slice(base.length)
    .replace(/^\/+|\/+$/g, "")
    .split("/")[0];

const withSearch = (path: string, params: URLSearchParams): string => {
  const search = params.toString();
  return search ? `${path}?${search}` : path;
};

/** A classic URL's place in the new scheduler. */
export function toV3Path(pathname: string, search: string): string {
  const page = subPage(pathname, CLASSIC);
  const params = new URLSearchParams(search);
  if (!SHARED_PAGES.has(page)) return withSearch(V3, params);
  // Classic's Day Sheet takes one `staffId`; V3's takes a comma list in `staff`.
  const staffId = params.get("staffId");
  if (page === "day-sheet" && staffId) {
    params.delete("staffId");
    params.set("staff", staffId);
  }
  return withSearch(`${V3}/${page}`, params);
}

/** A new-scheduler URL's place in the classic one. */
export function toClassicPath(pathname: string, search: string): string {
  const page = subPage(pathname, V3);
  const params = new URLSearchParams(search);
  if (!SHARED_PAGES.has(page)) return withSearch(CLASSIC, params);
  const staff = params.get("staff");
  if (page === "day-sheet" && staff) {
    params.delete("staff");
    params.set("staffId", staff.split(",")[0]);
  }
  return withSearch(`${CLASSIC}/${page}`, params);
}

export function hasClassicHandoffState(state: unknown): boolean {
  if (!state || typeof state !== "object") return false;
  return CLASSIC_HANDOFF_KEYS.some((key) => key in state);
}
